import { VideoPlayer } from '../../common/player';
import { Video2dContext, VideoGlContext } from '../video-context';
import { UpdateType, VideoNode, VideoNodeOptions } from './base';
import { isRequestVideoFrameCallbackSupported } from '../../common/rtc-detection';
import { empty, filter, fromEvent, pipe, startWith, Subject, subject, subscribe, switchMap, takeUntil } from 'fastrx';
import { IOS_MAIN_VERSION } from '../../utils/environment';
import { PlayerEvent } from '../../common/event';
import { ILogger, PlayerState } from '../../enums';

export class VideoImageSourceNode<S extends TexImageSource | VideoFrame = TexImageSource, C extends Video2dContext | VideoGlContext = Video2dContext> extends VideoNode<S, C> {
  private _lastImage: S;
  protected _totalFrames = 0;
  private _autoResize = false;
  private _canvasRendered?: Subject<TexImageSource>;
  videoCallbackId = 0;
  protected waitingFirstFrame = true;
  public shouldUpdate = true; // 合流时，希望 tryVideoFrameCallback 调用频率依赖其他节点帧率而不是自身时，设为 false
  constructor(context: C, options: Omit<VideoNodeOptions, 'name'>) {
    super(context, { name: 'imageSource', ...options });
    this._autoResize = options?.autoResize !== false;
    if (IOS_MAIN_VERSION === 16) {
      this._canvasRendered = subject<TexImageSource>();
      pipe(this._canvasRendered, startWith(this._image), switchMap(image => (image instanceof HTMLCanvasElement ? fromEvent(image, 'rendered') : empty())), takeUntil(fromEvent(this, 'closed')), subscribe(() => {
        this.update();
      }));
    }
  }
  onFirstFrame() {
    this.waitingFirstFrame = false;
  }
  tryVideoFrameCallback() {
    if (!this.shouldUpdate) return;
    const element: HTMLVideoElement = this.image as HTMLVideoElement;
    if (this.videoCallbackId) element.cancelVideoFrameCallback(this.videoCallbackId);
    if (isRequestVideoFrameCallbackSupported() && !document.hidden) {
      this.videoCallbackId = element.requestVideoFrameCallback((time, metadata) => {
        if (this.waitingFirstFrame) {
          this.onFirstFrame();
        }
        if (!document.hidden) {
          this._totalFrames = metadata.presentedFrames;
          // this.log.debug(this.width, this._totalFrames, this.totalFrames);
          this.update(UpdateType.UseTimerOnShow);
        }
      });
    }
  }
  private _render(seq: number, webgl: boolean) {
    let { width, height } = this;
    const { image } = this;
    if (image instanceof HTMLVideoElement) {
      this.tryVideoFrameCallback();
      ({ videoWidth: width, videoHeight: height } = image);
      if (!width || !height) return false;
      image.width = width;
      image.height = height;
    } else if (image instanceof HTMLImageElement || image instanceof ImageData || image instanceof ImageBitmap) {
      ({ width, height } = image);
      if (image !== this._lastImage) {
        this._lastImage = image;
      } else if (width === this.width && height === this.height) {
        return true;
      }
    } else if (image instanceof HTMLCanvasElement || image instanceof OffscreenCanvas) {
      ({ width, height } = image);
      this._lastImage = image;
    } else if (typeof VideoFrame !== 'undefined' && image instanceof VideoFrame) {
      ({ displayWidth: width, displayHeight: height } = image);
      (this._lastImage as VideoFrame)?.close();
      this._lastImage = image;
    }
    if (!this._autoResize) {
      return true;
    }
    if (this.width === width && this.height === height && this.totalFrames) {
      if (webgl) {
        this.useTexture();
        const gl = this.context.ctx as WebGL2RenderingContext;
        gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, gl.RGBA, gl.UNSIGNED_BYTE, image);
      }
    } else {
      if (webgl) {
        this.useTexture();
        const gl = this.context.ctx as WebGL2RenderingContext;
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
      }
      this.resize(width, height);
    }
    return true;
  }
  get image() {
    return this._image;
  }
  set image(value: S) {
    this._canvasRendered?.next(value);
    this._image = value;
  }
  render(seq: number) {
    return this._render(seq, true);
  }
  render2d(seq: number): boolean {
    return this._render(seq, false);
  }
}

export class VideoPlayerSourceNode<C extends Video2dContext | VideoGlContext = Video2dContext> extends VideoImageSourceNode<HTMLVideoElement, C> {
  constructor(context: C, protected _player: VideoPlayer, options: VideoNodeOptions) {
    super(context, options);
    this.name = 'videoPlayerSource';
    pipe(fromEvent(this._player, PlayerEvent.PLAYER_STATE_CHANGED), takeUntil(fromEvent(this, 'closed')), filter(({ state }) => state === PlayerState.PLAYING), subscribe(() => {
      this.tryVideoFrameCallback();
    }));
  }
  get image() {
    return this._player.element!;
  }
}

export class VideoTrackSourceNode<C extends Video2dContext | VideoGlContext = Video2dContext> extends VideoPlayerSourceNode<C> {
  get available() {
    return this._player.isPlaying && !this.waitingFirstFrame;
  }
  constructor(context: C, _videoTrack: MediaStreamVideoTrack, options: VideoNodeOptions) {
    super(context, new VideoPlayer({ id: options.name, track: _videoTrack, muted: true, container: null, objectFit: 'contain', log: options.logger }), options);
    this.name = 'videoTrackSource';
    // new MediaStreamTrackProcessor({ track: videoTrack }).readable.pipeTo(new WritableStream({
    //   write: (video: VideoFrame) => {
    //     this._currentFrame?.close();
    //     this._currentFrame = video.clone();
    //     video.close();
    //   }
    // }));
    this._player.play();
  }
  replaceTrack(track: MediaStreamVideoTrack) {
    this.waitingFirstFrame = true;
    this._player.setTrack(track);
    this._player.play();
  }
  close(): void {
    super.close();
    this._player.stop();
  }
}

export class TextSourceNode extends VideoNode<TexImageSource, Video2dContext> {
  hasChange = true;
  content = '';
  constructor(context: Video2dContext, textOptions: {
    content: string,
    font?: string,
    color?: string | CanvasGradient | CanvasPattern
  }, options: Omit<VideoNodeOptions, 'name'>) {
    super(context, { name: 'textSource', ...options, create2d: true });
    // this.ctx2d!.textAlign = 'left';
    this.ctx2d!.textBaseline = 'top';
    this.content = textOptions.content || '';
    if (textOptions.font) this.font = textOptions.font;
    if (textOptions.color) this.color = textOptions.color;
  }
  set font(font: string) {
    if (this.ctx2d) {
      this.ctx2d.font = font;
      this.hasChange = true;
    }
  }
  get font() {
    return this.ctx2d?.font || '';
  }
  set color(color: string | CanvasGradient | CanvasPattern) {
    if (this.ctx2d) {
      this.ctx2d.fillStyle = color;
      this.hasChange = true;
    }
  }
  get color() {
    return this.ctx2d?.fillStyle || '';
  }
  render2d(seq: number): boolean {
    if (this.ctx2d && this.hasChange) {
      this.ctx2d.clearRect(0, 0, this.width, this.height);
      this.drawMultilineText(0, 0);
      this.hasChange = false;
      return true;
    }
    return false;
  }
  render(seq: number): boolean {
    return false;
  }
  resize(width: number, height: number) {
    if (!this.ctx2d) return;
    // resize 会丢失画笔状态，需手动恢复。不能用 save/restore，因为 resize 会销毁画布上下文，保存的状态栈会被清空
    const { color, font } = this;
    super.resize(width, height);
    this.color = color;
    this.font = font;
  }
  drawMultilineText(x = 0, y = 0, lineHeightMultiplier = 1.2) {
    if (!this.ctx2d) return;
    // 加上基线离文字渲染方框顶部的距离
    const metrics = this.ctx2d.measureText(this.content);
    // 这两属性分别需要 chrome87+ 和 chrome77+
    y += metrics.fontBoundingBoxAscent || metrics.actualBoundingBoxAscent || 0;
    const match = this.font.match(/(\d+)px/);
    const fontSize = match ? parseInt(match[1], 10) : 16;
    const lineHeight = fontSize * lineHeightMultiplier;
    const lines = this.content.split('\n');
    for (let i = 0; i < lines.length; i++) {
      this.ctx2d.fillText(lines[i], x, y + (i * lineHeight));
    }
  }
}

// export class ShadowSourceNode<S extends TexImageSource = TexImageSource> extends VideoNode<S> {
//   constructor(context: VideoContext, public source: VideoNode<S>) {
//     super(context, { name: 'shadowSource', useDefaultProgram: false, createTexture: false, useFbo: false, matchInputSize: false });
//     this.texture = source.texture;
//     this.image = source.image;
//     this.width = source.width;
//     this.height = source.height;
//   }
//   resize(width: number, height: number) {
//     this.width = width;
//     this.height = height;
//     if (this.output && this.output.matchInputSize) this.output.resize(width, height);
//   }
//   render(seq: number) {
//     if (this.width !== this.source.width || this.height !== this.source.height) {
//       this.resize(this.source.width, this.source.height);
//     }
//     return true;
//   }
// }
